import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import {
  calculatePlaceHolderCount,
  useWindowResize,
} from "helpers/utility/utilities";
import classNames from "classnames";
import { connect } from "react-redux";

import { DataPreview, MetadataType, SetStateFunction } from "types";
import { State } from "redux/store";
import { getPreviewData } from "redux/CreateDataset/selectors";
import SquareInfoIcon from "assets/icons/SquareInfoIcon";
import { DATASET_CREATION_PAGE } from "helpers/constants/pages";
import EnrichmentCard from "views/pages/DataPage/CardsView/DataList/EnrichmentCard";

import styles from "./style.module.scss";
import VLTooltip from "views/uikit/VLTooltip";

const PreviewTitle = ({ previewCount }: { previewCount: number }) => {
  const renderPreviewCount = (count: number) => {
    const dynamicPreviewCount = `${count}  ${count > 1 ? "Images" : "Image"}`;
    return (
      <>
        <span className={styles.previewCount}>{dynamicPreviewCount}</span>
        <VLTooltip
          title={`Below is a preview of ${dynamicPreviewCount} generated from your data. You will be able to access full data once the graph indexing is completed.`}
          placement="right"
        >
          <div className={styles.infoIcon}>
            <SquareInfoIcon />
          </div>
        </VLTooltip>
      </>
    );
  };
  return (
    <div className={styles.previewTextContianer}>
      Preview{!!previewCount && renderPreviewCount(previewCount)}
    </div>
  );
};

const DataViewSection = ({ previewData }: DataViewProps) => {
  const [placeholderCount, setPlaceholderCount] = useState(0);

  const previewContainerRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const showPlaceholders = previewData.length < 1;
  const { height, width } = useWindowResize();
  const { newDatasetId } = useParams();

  const previewSectionClassNames = classNames(styles.previewSection, {
    [styles.hiddenOverflowY]: previewData.length < 1,
  });

  //empty functions
  const handleSelect = () => {};
  const handleSetVertex = () => {};
  const onGlobalMDTypeChange = () => {};
  const setIndexOfPointerOut: SetStateFunction<number> = () => {};

  useEffect(() => {
    const extraWidth = 60;

    let tempCount = calculatePlaceHolderCount(
      previewContainerRef.current,
      cardRef.current,
      previewData.length,
      extraWidth,
    );
    if (placeholderCount !== tempCount) {
      setPlaceholderCount(tempCount);
    }
  }, [height, width, placeholderCount, previewData.length]);

  return (
    <div className={styles.previewSectionContaniner}>
      <PreviewTitle previewCount={previewData.length} />

      <div className={previewSectionClassNames} ref={previewContainerRef}>
        {previewData.map((preview: DataPreview, index) => {
          const singleData = {
            ...preview,
            type: "IMAGE",
            media_id: index,
            media_uri: preview.thumbnail,
          };

          const globalMDType = preview.object_annotations?.length
            ? MetadataType.OBJECT_LABELS
            : MetadataType.IMAGE_LABELS;

          return (
            <EnrichmentCard
              key={index}
              index={index}
              clusterID={""}
              isVertex={false}
              isDataPage={false}
              modelType={"IMAGE"}
              isClusterPage={true}
              singleData={singleData}
              indexOfPointerOut={-10}
              showSelectOption={false}
              cardElementRef={cardRef}
              datasetId={newDatasetId!}
              globalMDType={globalMDType}
              showMetadataSummary={false}
              page={DATASET_CREATION_PAGE}
              containerRef={previewContainerRef}
              onSelect={handleSelect}
              onSetVertex={handleSetVertex}
              onIndexOfPointerOut={setIndexOfPointerOut}
              onGlobalMDTypeChange={onGlobalMDTypeChange}
            />
          );
        })}
        {showPlaceholders &&
          [...Array(placeholderCount)].map((_, index) => (
            <div key={index} className={styles.cardPlaceHolder}></div>
          ))}
      </div>
    </div>
  );
};

const mapStateToProps = (state: State) => {
  return { previewData: getPreviewData(state) };
};

export default connect(mapStateToProps)(DataViewSection);

interface DataViewProps {
  previewData: DataPreview[];
}
